import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ExternalLink,
  RotateCcw,
  ArrowUpCircle,
  Eye,
  Camera,
  FileText,
  Rocket,
  Globe,
  ListChecks,
  HelpCircle,
  Phone,
  TrendingUp,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import {
  useMe,
  useBrief,
  useRequestPreview,
  usePromote,
  useRollback,
  useBuildStatus,
} from '@/lib/brief';

export const Route = createFileRoute('/_app/')({
  staticData: { crumb: 'Pulpit' },
  component: DashboardPage,
});

/**
 * Clinic-owner dashboard — #130.
 *
 * Goal: in 5 seconds the owner knows
 *   1) where their site lives,
 *   2) what to do next,
 *   3) how to get help.
 *
 * Layout (above-the-fold first):
 *   - Site hero card — preview iframe + open/edit CTAs + version stamp
 *   - Checklist (5 items) — auto-tick from brief + tenant state
 *   - This-week metrics — visits / leads / phone clicks (graceful "—")
 *   - Publish controls — preview/promote/rollback
 *   - Help — link to /help inbox + email
 */
function DashboardPage() {
  const { session } = Route.useRouteContext();
  if (session.role === 'operator') return null;

  const me = useMe();
  const brief = useBrief();
  const buildStatus = useBuildStatus();
  const requestPreview = useRequestPreview();
  const promote = usePromote();
  const rollback = useRollback();

  const tenant = me.data?.tenant;
  const onPreview = async () => {
    try {
      const r = await requestPreview.mutateAsync();
      toast.success(
        `Zlecono budowę podglądu. Po wygenerowaniu dostępny pod ${(r as { preview_url?: string }).preview_url ?? ''}.`,
      );
    } catch {
      toast.error('Nie udało się zlecić podglądu.');
    }
  };
  const onPromote = async () => {
    try {
      await promote.mutateAsync();
      toast.success('Opublikowano nową wersję strony.');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const onRollback = async () => {
    try {
      await rollback.mutateAsync();
      toast.success('Cofnięto do poprzedniej wersji.');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const previewVersion = tenant?.preview_version ?? 0;
  const liveVersion = tenant?.live_version ?? null;
  const previewHost = tenant?.preview_token
    ? `${tenant.preview_token}.pozyskajpacjenta.pl`
    : null;
  const slugHost = tenant?.slug ? `${tenant.slug}.pozyskajpacjenta.pl` : null;
  const liveHost = tenant?.custom_host ?? slugHost;
  // What the visitor actually sees → prefer live, fall back to preview-token.
  const publicHost = liveVersion ? liveHost : previewHost;
  const publicUrl = publicHost ? `https://${publicHost}` : null;

  // Checklist mirrors the 5-step structure from issue #130. Each step has
  // a deterministic "done" rule and a deep-link to where to complete it.
  const b = brief.data ?? {};
  const photoCount =
    (b as { photos_uploaded?: number }).photos_uploaded ??
    Number.isFinite((b as { media_count?: number }).media_count)
      ? Number((b as { media_count?: number }).media_count ?? 0)
      : 0;
  const checklist: Array<{
    id: string;
    label: string;
    hint?: string;
    done: boolean;
    icon: React.ComponentType<{ className?: string }>;
    to: string;
  }> = [
    {
      id: 'photos',
      label: 'Dodaj 6 zdjęć kliniki',
      hint: `${Math.min(6, photoCount)} z 6 wgranych`,
      done: photoCount >= 6,
      icon: Camera,
      to: '/media',
    },
    {
      id: 'basics',
      label: 'Uzupełnij dane podstawowe (NIP, adres, godziny)',
      done: !!(
        (b as { legal?: { nip?: string } })?.legal?.nip &&
        (b as { address?: { city?: string } })?.address?.city &&
        ((b as { hours?: unknown[] })?.hours?.length ?? 0) > 0
      ),
      icon: FileText,
      to: '/brief',
    },
    {
      id: 'team',
      label: 'Dodaj zespół (min. 2 osoby ze zdjęciami)',
      hint:
        ((b as { team?: Array<{ photo_url?: string }> })?.team?.filter((m) => m.photo_url) ?? [])
          .length + ' osób ze zdjęciami',
      done:
        ((b as { team?: Array<{ photo_url?: string }> })?.team?.filter((m) => m.photo_url) ?? [])
          .length >= 2,
      icon: FileText,
      to: '/brief/team',
    },
    {
      id: 'publish',
      label: 'Pierwsza publikacja',
      hint: liveVersion ? `live: wersja ${liveVersion}` : 'po opublikowaniu strona staje się dostępna',
      done: !!liveVersion,
      icon: Rocket,
      to: '/publish',
    },
    {
      id: 'domain',
      label: 'Podłącz własną domenę (opcjonalne)',
      hint: tenant?.custom_host ?? 'np. klinika.pl',
      done: !!tenant?.custom_host,
      icon: Globe,
      to: '/settings',
    },
  ];

  const checklistDone = checklist.filter((c) => c.done).length;
  const checklistPct = Math.round((checklistDone / checklist.length) * 100);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Witaj{tenant?.legal_name ? `, ${tenant.legal_name}` : ''}
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Stan strony, najbliższe zadania i skrót do publikacji.
        </p>
      </header>

      {/* Hero: site-at-a-glance ─ preview iframe + open/edit CTAs */}
      <Card>
        <CardContent className="p-0">
          <div className="grid gap-0 md:grid-cols-[minmax(0,5fr)_minmax(0,4fr)]">
            {/* Left: iframe thumbnail */}
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-surface md:aspect-auto md:min-h-[280px]">
              {publicUrl ? (
                <iframe
                  src={publicUrl}
                  title={`Podgląd ${tenant?.legal_name ?? 'strony'}`}
                  className="size-full origin-top-left scale-[0.5] border-0"
                  style={{ width: '200%', height: '200%' }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="flex size-full items-center justify-center text-sm text-text-subtle">
                  Wygeneruj pierwszy podgląd
                </div>
              )}
            </div>
            {/* Right: copy + CTAs */}
            <div className="flex flex-col justify-center gap-4 p-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">
                  Twoja strona
                </p>
                <p className="mt-2 font-display text-lg font-semibold text-text">
                  {publicHost ?? 'jeszcze nie wygenerowano'}
                </p>
                <p className="mt-1 text-xs text-text-muted">
                  {liveVersion
                    ? `live: wersja ${liveVersion}`
                    : previewVersion > 0
                      ? `podgląd: wersja ${previewVersion} — opublikuj, by stronę zobaczyli pacjenci`
                      : 'pierwsza budowa w toku — wkrótce'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {publicUrl ? (
                  <Button asChild size="sm">
                    <a href={publicUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="size-4" aria-hidden="true" />
                      Otwórz stronę
                    </a>
                  </Button>
                ) : null}
                <Button asChild variant="secondary" size="sm">
                  <Link to={'/brief' as never}>
                    <FileText className="size-4" aria-hidden="true" />
                    Edytuj treść
                  </Link>
                </Button>
                <Button asChild variant="ghost" size="sm">
                  <Link to={'/media' as never}>
                    <Camera className="size-4" aria-hidden="true" />
                    Zdjęcia
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checklist ─ progress bar + 5 actionable rows */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ListChecks className="size-4 text-accent" aria-hidden="true" />
              Następne kroki
            </CardTitle>
            <CardDescription>
              {checklistDone === checklist.length
                ? 'Wszystkie kroki ukończone. Strona jest gotowa.'
                : `${checklistDone} z ${checklist.length} ukończone — kontynuuj poniżej.`}
            </CardDescription>
          </div>
          <div className="hidden md:flex md:w-40 md:flex-col md:items-end md:gap-1">
            <span className="text-xs text-text-subtle tabular-nums">{checklistPct}%</span>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg ring-1 ring-border">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${checklistPct}%` }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ul role="list" className="space-y-2 text-sm">
            {checklist.map((step) => {
              const Icon = step.icon;
              return (
                <li key={step.id}>
                  <Link
                    to={step.to as never}
                    className="flex items-center justify-between gap-3 rounded-md bg-surface p-3 ring-1 ring-black/5 hover:ring-black/10"
                  >
                    <span className="flex items-center gap-3">
                      <span
                        aria-hidden="true"
                        className={
                          step.done
                            ? 'inline-grid size-7 place-items-center rounded-full bg-success/20 text-success'
                            : 'inline-grid size-7 place-items-center rounded-full bg-bg ring-1 ring-border'
                        }
                      >
                        {step.done ? '✓' : <Icon className="size-3.5 text-text-muted" />}
                      </span>
                      <span className="flex flex-col gap-0.5">
                        <span className={step.done ? 'text-text-muted line-through' : 'text-text'}>
                          {step.label}
                        </span>
                        {step.hint ? (
                          <span className="text-xs text-text-subtle">{step.hint}</span>
                        ) : null}
                      </span>
                    </span>
                    <span className="text-xs text-text-subtle">otwórz →</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* This-week metrics — graceful empty state until data exists */}
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          icon={Eye}
          label="Odwiedziny (7 dni)"
          value={liveVersion ? '—' : 'po publikacji'}
          hint="dane z GA4 — pojawiają się po pierwszej publikacji"
        />
        <MetricCard
          icon={FileText}
          label="Wiadomości (7 dni)"
          value={liveVersion ? '—' : 'po publikacji'}
          hint="z formularza /kontakt"
        />
        <MetricCard
          icon={Phone}
          label="Kliknięcia w telefon"
          value={liveVersion ? '—' : 'po publikacji'}
          hint="tap-to-call na mobile"
        />
      </div>

      {/* Publish controls */}
      <Card>
        <CardHeader>
          <CardTitle>Publikacja</CardTitle>
          <CardDescription>
            Po edycji treści zleć budowę podglądu, sprawdź wynik i opublikuj.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={onPreview} disabled={requestPreview.isPending}>
              <Eye className="size-4" aria-hidden="true" />
              {requestPreview.isPending ? 'Zlecanie…' : 'Zleć budowę podglądu'}
            </Button>
            <Button
              variant="secondary"
              onClick={onPromote}
              disabled={previewVersion <= 0 || promote.isPending || previewVersion === liveVersion}
            >
              <ArrowUpCircle className="size-4" aria-hidden="true" />
              Opublikuj jako live
            </Button>
            <Button
              variant="ghost"
              onClick={onRollback}
              disabled={!liveVersion || liveVersion <= 1 || rollback.isPending}
            >
              <RotateCcw className="size-4" aria-hidden="true" />
              Cofnij wersję
            </Button>
            <Link
              to={'/publish' as never}
              className="ml-auto text-sm text-accent hover:underline"
            >
              Pełna historia wersji →
            </Link>
          </div>
          {buildStatus.data?.latest ? (
            <div className="rounded-md bg-surface p-4 ring-1 ring-black/5">
              <p className="text-xs uppercase tracking-wide text-text-subtle">
                Ostatnia budowa
              </p>
              <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-display text-base font-semibold text-text">
                  wersja {buildStatus.data.latest.version}
                </p>
                <BuildStatusBadge status={buildStatus.data.latest.status} />
              </div>
              {buildStatus.data.latest.status === 'failed' && buildStatus.data.latest.error ? (
                <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-bg p-2 text-xs text-error ring-1 ring-error/20">
                  {buildStatus.data.latest.error}
                </pre>
              ) : null}
              {buildStatus.data.latest.status === 'ready' &&
              buildStatus.data.latest.page_count ? (
                <p className="mt-1 text-xs text-text-muted">
                  Zbudowano {buildStatus.data.latest.page_count} stron.
                </p>
              ) : null}
              {buildStatus.data.latest.status === 'requested' ||
              buildStatus.data.latest.status === 'building' ? (
                <p className="mt-2 text-xs text-text-muted">
                  Budowa w toku — odświeżamy automatycznie co 10 sekund.
                </p>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Help — link to /help inbox + email */}
      <Card>
        <CardContent className="flex flex-col items-start justify-between gap-3 p-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="inline-grid size-9 place-items-center rounded-full bg-accent/10 text-accent">
              <HelpCircle className="size-4" aria-hidden="true" />
            </span>
            <div>
              <p className="font-medium text-text">Potrzebujesz pomocy?</p>
              <p className="text-xs text-text-muted">
                Wiadomość od Was → odpowiadamy w 24 h. Pilne — zadzwoń.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <a href="mailto:max@pozyskajpacjenta.pl">
                <FileText className="size-4" aria-hidden="true" />
                max@pozyskajpacjenta.pl
              </a>
            </Button>
            <Button asChild size="sm">
              <Link to={'/help' as never}>
                <TrendingUp className="size-4" aria-hidden="true" />
                Napisz wiadomość
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-text-subtle">
          <Icon className="size-3.5 text-accent" aria-hidden="true" />
          {label}
        </div>
        <p className="mt-2 font-display text-2xl font-semibold tabular-nums text-text">
          {value}
        </p>
        <p className="mt-1 text-xs text-text-muted">{hint}</p>
      </CardContent>
    </Card>
  );
}

function BuildStatusBadge({
  status,
}: {
  status: 'requested' | 'building' | 'ready' | 'failed';
}) {
  const meta: Record<
    typeof status,
    { variant: 'warning' | 'soft' | 'success' | 'destructive'; label: string }
  > = {
    requested: { variant: 'warning', label: 'w kolejce' },
    building: { variant: 'soft', label: 'budowanie…' },
    ready: { variant: 'success', label: 'gotowe' },
    failed: { variant: 'destructive', label: 'błąd' },
  };
  const m = meta[status];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}
